import { create } from 'zustand';
import { ApiError, Notification } from '../types';

export type ModalType = 
  | 'adventure-details'
  | 'character-selection'
  | 'session-settings'
  | 'player-profile'
  | 'error-details'
  | 'confirmation'
  | null;

export type ViewType = 
  | 'home'
  | 'lobby'
  | 'adventure-selection'
  | 'character-selection'
  | 'session-lobby'
  | 'game'
  | 'scene'
  | 'profile';

interface ModalData {
  type: ModalType;
  title?: string;
  content?: React.ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  data?: any;
}

interface UIState {
  // Loading states
  isLoading: boolean;
  loadingMessage: string | null;
  
  // Action pending (option selection in progress) and escalation for slow-response UI
  actionPendingSince: number | null;
  disableAllInteractions: boolean;
  showProblemOverlay: boolean;
  escalationTimer500Id: number | null;
  escalationTimer3000Id: number | null;

  // Error handling
  error: ApiError | null;
  errorMessage: string | null;
  
  // Modal and notification states
  activeModal: ModalData | null;
  notifications: Notification[];
  
  // Mobile-specific UI state
  sidebarOpen: boolean;
  currentView: ViewType;
  
  // Toast notifications
  toasts: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    duration?: number;
    isLeaving?: boolean;
  }>;
  
  // Actions
  setLoading: (loading: boolean, message?: string) => void;
  setError: (error: ApiError | null, message?: string) => void;
  showModal: (modal: ModalData) => void;
  hideModal: () => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  setSidebarOpen: (open: boolean) => void;
  setCurrentView: (view: ViewType) => void;
  clearError: () => void;
  setActionPending: (pending: boolean) => void;
  setDisableAllInteractions: (value: boolean) => void;
  setShowProblemOverlay: (value: boolean) => void;
  setEscalationTimers: (id500: number | null, id3000: number | null) => void;
  clearActionPending: () => void;
  
  // Toast actions
  showToast: (message: string, type?: 'success' | 'error' | 'warning' | 'info', duration?: number) => void;
  hideToast: (id: string) => void;
  clearToasts: () => void;
}

const useUIStore = create<UIState>((set, get) => ({
  // Initial state
  isLoading: false,
  loadingMessage: null,
  actionPendingSince: null,
  disableAllInteractions: false,
  showProblemOverlay: false,
  escalationTimer500Id: null,
  escalationTimer3000Id: null,
  error: null,
  errorMessage: null,
  activeModal: null,
  notifications: [],
  sidebarOpen: false,
  currentView: 'home',
  toasts: [],

  // Actions
  setLoading: (loading: boolean, message?: string) => {
    set({
      isLoading: loading,
      loadingMessage: message || null,
    });
  },

  setError: (error: ApiError | null, message?: string) => {
    set({
      error,
      errorMessage: message || error?.message || null,
    });
    
    // Auto-hide error after 5 seconds
    if (error) {
      setTimeout(() => {
        get().clearError();
      }, 5000);
    }
  },

  showModal: (modal: ModalData) => {
    set({ activeModal: modal });
  },

  hideModal: () => {
    set({ activeModal: null });
  },

  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notification_${Date.now()}_${Math.random()}`,
      timestamp: new Date(),
    };
    
    set(state => ({
      notifications: [...state.notifications, newNotification],
    }));
    
    // Auto-hide notification if autoHide is true (default)
    if (notification.autoHide !== false) {
      setTimeout(() => {
        get().removeNotification(newNotification.id);
      }, 5000);
    }
  },

  removeNotification: (id: string) => {
    set(state => ({
      notifications: state.notifications.filter(notification => notification.id !== id),
    }));
  },

  clearNotifications: () => {
    set({ notifications: [] });
  },

  setSidebarOpen: (open: boolean) => {
    set({ sidebarOpen: open });
  },

  setCurrentView: (view: ViewType) => {
    set({ currentView: view });
  },

  clearError: () => {
    set({
      error: null,
      errorMessage: null,
    });
  },

  setActionPending: (pending: boolean) => {
    const state = get();
    if (state.escalationTimer500Id != null) clearTimeout(state.escalationTimer500Id);
    if (state.escalationTimer3000Id != null) clearTimeout(state.escalationTimer3000Id);
    set({
      actionPendingSince: pending ? Date.now() : null,
      disableAllInteractions: false,
      showProblemOverlay: false,
      escalationTimer500Id: null,
      escalationTimer3000Id: null,
    });
  },

  setDisableAllInteractions: (value: boolean) => {
    set({ disableAllInteractions: value });
  },

  setShowProblemOverlay: (value: boolean) => {
    set({ showProblemOverlay: value });
  },

  setEscalationTimers: (id500: number | null, id3000: number | null) => {
    set({ escalationTimer500Id: id500, escalationTimer3000Id: id3000 });
  },

  clearActionPending: () => {
    const state = get();
    if (state.escalationTimer500Id != null) clearTimeout(state.escalationTimer500Id);
    if (state.escalationTimer3000Id != null) clearTimeout(state.escalationTimer3000Id);
    set({
      actionPendingSince: null,
      disableAllInteractions: false,
      showProblemOverlay: false,
      escalationTimer500Id: null,
      escalationTimer3000Id: null,
      isLoading: false,
    });
  },

  // Toast actions
  showToast: (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', duration: number = 3000) => {
    const toast = {
      id: `toast_${Date.now()}_${Math.random()}`,
      type,
      message,
      duration,
    };
    
    set(state => ({
      toasts: [...state.toasts, toast],
    }));
    
    // Auto-hide toast with fade-out animation
    setTimeout(() => {
      // Trigger fade-out by setting a flag, then remove after animation
      const currentToasts = get().toasts;
      const toastIndex = currentToasts.findIndex(t => t.id === toast.id);
      if (toastIndex >= 0) {
        // Mark toast as leaving
        const updatedToasts = [...currentToasts];
        const existingToast = updatedToasts[toastIndex];
        if (existingToast) {
          updatedToasts[toastIndex] = { ...existingToast, isLeaving: true };
          set({ toasts: updatedToasts });
        }
        
        // Remove after fade-out animation
        setTimeout(() => {
          get().hideToast(toast.id);
        }, 600);
      }
    }, duration);
  },

  hideToast: (id: string) => {
    const currentToasts = get().toasts;
    const toastIndex = currentToasts.findIndex(t => t.id === id);
    
    if (toastIndex >= 0) {
      const toast = currentToasts[toastIndex];
      
      // If toast is already leaving, remove it immediately
      if (toast?.isLeaving) {
        set(state => ({
          toasts: state.toasts.filter(toast => toast.id !== id),
        }));
      } else {
        // Start fade-out animation
        const updatedToasts = [...currentToasts];
        const existingToast = updatedToasts[toastIndex];
        if (existingToast) {
          updatedToasts[toastIndex] = { ...existingToast, isLeaving: true };
          set({ toasts: updatedToasts });
        }
        
        // Remove after fade-out animation
        setTimeout(() => {
          set(state => ({
            toasts: state.toasts.filter(toast => toast.id !== id),
          }));
        }, 600);
      }
    }
  },

  clearToasts: () => {
    set({ toasts: [] });
  },
}));

export default useUIStore;
